import type {
  BoxTreeNodeComponentProps,
  BoxTreeNodePath,
} from 'components/BoxTree/BoxTreeNodeComponentProps';
import type { ComponentType } from 'react';

export type TreeOrientation = 'horizontal' | 'vertical';
export interface ConnectionLinesStyle {
  className?: string | null;
  thickness?: number;
}

export type UseChildrenContextHook<TNode, TContext = undefined> = (
  context: TContext,
  node: TNode,
  path: BoxTreeNodePath,
) => TContext;

export interface BoxTreeHocProps<TNode, TContext = undefined> {
  getChildren: (node: TNode, context: TContext) => TNode[];
  /**
   * Orientation in which child nodes are rendered
   *
   * If not present, defaults to 'vertical'.
   *
   * @param node null should give orientation of root nodes
   */
  getChildrenOrientation?: (node: TNode | null) => TreeOrientation;
  /**
   * Can be used to provide color of lines connecting this node with its children
   *
   * This is advised to be used only for changing color etc,
   * customizing layout properties may break the overall tree layout.
   *
   * Return null to use default style.
   */
  getChildrenLinesStyle?: (
    node: TNode,
    context: TContext,
    path: BoxTreeNodePath,
  ) => ConnectionLinesStyle | null;
  /**
   * Thickness of lines to children (default 1)
   */
  getChildrenLinesThickness?: (node: TNode) => number | null;
  nodeComponent: ComponentType<BoxTreeNodeComponentProps<TNode, TContext>>;
  /**
   * Customize context for subtree of given node
   *
   * The function is used as a hook, so you can easily use useMemo etc.
   * useContext in the hook is discouraged as many context consumers kill performance.
   * When this function is not specified, all nodes receive the same context.
   */
  useChildrenContext?: UseChildrenContextHook<TNode, TContext>;
}

export const CHILDREN_ORIENTATION_DEFAULT: TreeOrientation = 'vertical';
